import {
  Component,
  ViewChild,
  ViewContainerRef,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  ChangeDetectorRef,
} from "@angular/core";
import {
  DrawerConfig,
  DefaultDrawerConfig,
  DrawerService,
} from "../../service/drawer.service";
import { StaticResourceService } from "../../service/static-resource.service";

type DrawerComponentCache = {
  ref: ComponentRef<any>;
  config: DrawerConfig;
  toolbarEl: HTMLElement;
  headerEl: HTMLElement;
  topEl: HTMLElement;
};

@Component({
  selector: "rs-drawer",
  templateUrl: "./index.component.html",
  styleUrls: ["./index.component.scss"],
})
export class DrawerComponent {
  @ViewChild("element", { static: false }) element: ElementRef<HTMLDivElement>;
  @ViewChild("drawer", { static: false }) drawer: ElementRef<HTMLDivElement>;
  @ViewChild("header", { static: false }) header: ElementRef<HTMLDivElement>;
  @ViewChild("content", { static: false }) content: ElementRef<HTMLDivElement>;

  @ViewChild("toolbarSlot", { static: false })
  toolbarSlot: ElementRef<HTMLDivElement>;
  @ViewChild("headerSlot", { static: false })
  headerSlot: ElementRef<HTMLDivElement>;
  @ViewChild("topSlot", { static: false }) topSlot: ElementRef<HTMLDivElement>;

  @ViewChild("dynamicComponentContainer", {
    read: ViewContainerRef,
    static: false,
  })
  private container: ViewContainerRef;

  config: DrawerConfig = DefaultDrawerConfig;
  useAnimation: "yes" | "no" = "yes";
  isOpened = false;
  hiddenDrawer = true;

  private componentRefMap = new Map<string, DrawerComponentCache[]>();

  constructor(
    private resolver: ComponentFactoryResolver,
    private service: DrawerService,
    private ref: ChangeDetectorRef,
  ) {}

  private createDynamicComponent(component: new (...args: any[]) => any) {
    // 确保在调用之前目标组件已经在entryComponents中声明
    const factory = this.resolver.resolveComponentFactory(component);
    const componentRef = this.container.createComponent(factory);
    return componentRef;
  }

  private destroyDynamicComponent(componentRef: ComponentRef<any>) {
    if (componentRef) {
      componentRef.destroy();
    }
  }

  private setComponentData(
    componentRef: ComponentRef<any>,
    data: Record<string, any> = {},
  ) {
    // 如果需要传递数据给动态组件，可以这样做
    if (componentRef) {
      Object.entries(data).forEach(([key, value]) => {
        componentRef.instance[key] = value;
      });
    }
  }

  private getSlotElement(componentRef: ComponentRef<any>, selector: string) {
    const componentEl = componentRef.location.nativeElement;
    return componentEl.querySelector(selector) as HTMLElement;
  }

  private setSlotElement(slotEl: HTMLElement, containerEl: HTMLElement) {
    containerEl.innerHTML = "";
    if (slotEl) {
      containerEl.append(slotEl);
    }
  }

  setCache(cache: DrawerComponentCache) {
    this.setSlotElement(cache.toolbarEl, this.toolbarSlot.nativeElement);
    this.setSlotElement(cache.headerEl, this.headerSlot.nativeElement);
    this.setSlotElement(cache.topEl, this.topSlot.nativeElement);

    const drawerTop = cache.config.top ? `${cache.config.top}px` : "";
    this.element.nativeElement.style.setProperty("--drawer-top", drawerTop);

    if (cache.config.mode === "inner") {
      this.content.nativeElement.insertBefore(
        this.header.nativeElement,
        this.content.nativeElement.firstChild,
      );
      this.content.nativeElement.append(this.toolbarSlot.nativeElement);
    } else {
      this.drawer.nativeElement.insertBefore(
        this.header.nativeElement,
        this.drawer.nativeElement.firstChild,
      );
      this.header.nativeElement.append(this.toolbarSlot.nativeElement);
    }

    this.config = cache.config;
    this.ref.markForCheck();
  }

  setConfig(config: Partial<DrawerConfig>) {
    const caches = this.componentRefMap.get(this.service.cacheKey) || [];
    const cache = caches.slice(-1)[0];
    if (cache) {
      cache.config = { ...cache.config, ...config };
      this.setCache(cache);
    }
  }

  show<T>(
    component: new (...args: any[]) => T,
    config: DrawerConfig,
    data: Record<string, any>,
  ): T {
    const showConfig = { ...DefaultDrawerConfig, ...config };
    const componentRef = this.createDynamicComponent(component);
    const toolbarEl = this.getSlotElement(componentRef, "[drawer-toolbar]");
    const headerEl = this.getSlotElement(componentRef, "[drawer-header]");
    const topEl = this.getSlotElement(componentRef, "[drawer-top]");
    const cache = {
      config: showConfig,
      ref: componentRef,
      toolbarEl,
      headerEl,
      topEl,
    };

    const caches = this.componentRefMap.get(this.service.cacheKey) || [];
    this.componentRefMap.set(this.service.cacheKey, [...caches, cache]);
    // 如果是第一个缓存，打开抽屉
    if (caches.length === 0) {
      this.toggleOpenStatus(true, true);
    }
    this.toggleCache(cache);
    this.setComponentData(componentRef, data);

    return componentRef.instance;
  }

  hide() {
    this.service.hide();
  }

  back() {
    this.deleteCache(this.service.cacheKey);

    // 如果有上一个缓存，设置为当前缓存，否则关闭抽屉
    const caches = this.componentRefMap.get(this.service.cacheKey) || [];
    const cache = caches.slice(-1)[0];
    if (cache) {
      this.toggleCache(cache);
    } else {
      this.toggleOpenStatus(false, true);
    }
  }

  private deleteCache(cacheKey: string) {
    const caches = this.componentRefMap.get(cacheKey) || [];
    const cache = caches.slice(-1)[0];
    if (cache) {
      this.destroyDynamicComponent(cache.ref);
      caches.pop();
    }
    if (caches.length === 0) {
      this.componentRefMap.delete(cacheKey);
    }
  }

  private toggleCache(cache: DrawerComponentCache) {
    this.componentRefMap.forEach((caches) => {
      caches.forEach((c) => {
        c.ref.location.nativeElement.style.display = c === cache ? "" : "none";
      });
    });
    this.setCache(cache);
  }

  deleteAllCache(cacheKey: string) {
    const caches = this.componentRefMap.get(cacheKey) || [];
    caches.forEach((cache) => {
      this.destroyDynamicComponent(cache.ref);
    });
    this.componentRefMap.delete(cacheKey);
  }

  onRouteChange() {
    const caches = this.componentRefMap.get(this.service.cacheKey) || [];
    const cache = caches.slice(-1)[0];
    if (cache) {
      this.toggleCache(cache);
      this.toggleOpenStatus(true);
    } else {
      this.toggleOpenStatus(false);
    }
    this.useAnimation = "no";
  }

  toggleOpenStatus(isOpen: boolean, useAnimation = false) {
    this.isOpened = isOpen;
    this.useAnimation = useAnimation ? "yes" : "no";
    this.hiddenDrawer = !isOpen && !useAnimation;
    this.ref.markForCheck();
  }

  onAnimationEnd() {
    if (!this.isOpened) {
      this.hiddenDrawer = true;
      this.ref.markForCheck();
    }
  }
}
